import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  CircleHelp,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { BaseGameProps } from "../common/types";
import { useGamesPortal } from "@/features/games-portal/hooks/use-games-portal";
import { COLS, DAS_MS, ARR_MS, HIGH_SCORE_KEY, VISIBLE_ROWS } from "./constants";
import { tetrisAudio } from "./audio";
import { TetrisEffects, type BoardLayout } from "./effects";
import { TetrisEngine } from "./engine";
import { haptic } from "./haptics";
import { drawFrame, drawPiecePreview } from "./render";
import type { EngineEvent, EngineSnapshot, PieceId } from "./types";
import { useTetrisGestures } from "./use-tetris-gestures";

const CONTROL_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowDown",
  "ArrowUp",
  " ",
  "w",
  "W",
  "a",
  "A",
  "s",
  "S",
  "d",
  "D",
  "z",
  "Z",
  "x",
  "X",
  "c",
  "C",
  "p",
  "P",
  "Shift",
]);

function readHighScore(): number {
  if (typeof window === "undefined") return 0;
  return Number(window.localStorage.getItem(HIGH_SCORE_KEY) || 0);
}

function persistHighScore(score: number): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(HIGH_SCORE_KEY, String(score));
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

function PiecePreview({
  piece,
  label,
  dimmed,
}: {
  piece: PieceId | null;
  label: string;
  dimmed?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const size = 64;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const dark = document.documentElement.classList.contains("dark");
    drawPiecePreview(ctx, piece, size, dark);
  }, [piece]);

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-1 rounded-xl border px-2 py-1.5 backdrop-blur-xl",
        "border-white/40 bg-white/55 dark:border-white/10 dark:bg-gray-900/55",
        dimmed && "opacity-50"
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary-600 dark:text-gray-400">
        {label}
      </span>
      <canvas ref={canvasRef} className="h-12 w-12" />
    </div>
  );
}

export function Tetris({
  className = "",
  difficulty = "medium",
  onGameEnd,
}: BaseGameProps) {
  const { t } = useTranslation();
  const { isCollapsed, setSessionHud } = useGamesPortal();
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gestureRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<TetrisEngine | null>(null);
  const effectsRef = useRef(new TetrisEffects());
  const layoutRef = useRef<BoardLayout>({
    originX: 0,
    originY: 0,
    cell: 20,
    width: 200,
    height: 400,
  });
  const keysRef = useRef({
    left: false,
    right: false,
    down: false,
    dasLeft: 0,
    dasRight: 0,
    arrLeft: 0,
    arrRight: 0,
  });

  const [snapshot, setSnapshot] = useState<EngineSnapshot>(() =>
    new TetrisEngine(difficulty, readHighScore()).getSnapshot()
  );
  const [muted, setMuted] = useState(() => tetrisAudio.isMuted());
  const [banner, setBanner] = useState<string | null>(null);
  const [narrow, setNarrow] = useState(true);
  const [touch, setTouch] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const syncHud = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    setSnapshot(engine.getSnapshot());
  }, []);

  const eventHandlerRef = useRef<(event: EngineEvent) => void>(() => {});
  eventHandlerRef.current = (event) => {
    const engine = engineRef.current;
    if (!engine) return;
    const layout = layoutRef.current;
    const effects = effectsRef.current;

    if (event.type === "move") tetrisAudio.play("move");
    if (event.type === "rotate") {
      tetrisAudio.play("rotate");
      haptic("rotate");
    }
    if (event.type === "hold") tetrisAudio.play("hold");
    if (event.type === "soft-drop") tetrisAudio.play("soft");
    if (event.type === "lock" && event.cells && event.piece) {
      tetrisAudio.play("lock");
      haptic("lock");
      effects.burstLock(event.cells, event.piece, layout);
    }
    if (event.type === "hard-drop" && event.cells && event.piece) {
      tetrisAudio.play("hard");
      effects.burstHardDrop(
        event.cells,
        event.piece,
        event.dropDistance ?? 0,
        layout
      );
    }
    if (event.type === "clear" && event.clear) {
      if (event.clear.isTetris) {
        tetrisAudio.play("tetris");
        haptic("tetris");
      } else if (event.clear.tSpin !== "none") {
        tetrisAudio.play("tspin");
        haptic("clear");
      } else {
        tetrisAudio.play("clear");
        haptic("clear");
      }
      effects.burstClear(event.clear, layout, event.piece);
      if (event.clear.isTetris) {
        setBanner(t("games.tetris.tetris"));
      } else if (event.clear.tSpin !== "none") {
        setBanner(t("games.tetris.tSpin"));
      } else if (event.clear.combo > 1) {
        setBanner(t("games.tetris.comboBanner", { count: event.clear.combo }));
      }
    }
    if (event.type === "game-over") {
      tetrisAudio.play("over");
      haptic("over");
      persistHighScore(engine.getSnapshot().highScore);
      onGameEnd?.("lose");
    }
    syncHud();
  };

  const restart = useCallback(() => {
    const engine = new TetrisEngine(difficulty, readHighScore());
    engineRef.current = engine;
    engine.on((event) => eventHandlerRef.current(event));
    effectsRef.current = new TetrisEffects();
    setBanner(null);
    setSnapshot(engine.getSnapshot());
  }, [difficulty]);

  useEffect(() => {
    restart();
  }, [restart]);

  // Dock küçülünce veya sekme gizlenince state korunur, oyun duraklatılır
  useEffect(() => {
    if (!isCollapsed) return;
    engineRef.current?.pause();
    keysRef.current.left = false;
    keysRef.current.right = false;
    keysRef.current.down = false;
    syncHud();
  }, [isCollapsed, syncHud]);

  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) return;
      engineRef.current?.pause();
      keysRef.current.left = false;
      keysRef.current.right = false;
      keysRef.current.down = false;
      syncHud();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [syncHud]);

  // Oynarken ekranın kapanmasını geciktir (destekleyen tarayıcılarda)
  useEffect(() => {
    const canLock = typeof navigator !== "undefined" && "wakeLock" in navigator;
    if (!canLock || snapshot.status !== "playing" || isCollapsed) return;

    let released = false;
    let lock: WakeLockSentinel | null = null;

    const request = async () => {
      if (document.hidden || released) return;
      try {
        lock = await navigator.wakeLock.request("screen");
      } catch {
        // Kullanıcı jesti yoksa veya destek kesildiyse yoksay
      }
    };

    void request();
    const onVisible = () => {
      if (!document.hidden) void request();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release();
    };
  }, [isCollapsed, snapshot.status]);

  useTetrisGestures(
    gestureRef,
    engineRef,
    keysRef,
    touch && snapshot.status === "playing" && !isCollapsed && !helpOpen
  );

  useEffect(() => {
    setSessionHud({
      score: snapshot.score,
      level: snapshot.level,
      highScore: snapshot.highScore,
      status: snapshot.status,
    });
  }, [
    setSessionHud,
    snapshot.highScore,
    snapshot.level,
    snapshot.score,
    snapshot.status,
  ]);

  useEffect(() => {
    return () => setSessionHud(null);
  }, [setSessionHud]);

  useEffect(() => {
    if (!banner || !bannerRef.current) return;
    const el = bannerRef.current;
    const tween = gsap.fromTo(
      el,
      { scale: 0.55, opacity: 0, y: 18 },
      {
        scale: 1,
        opacity: 1,
        y: 0,
        duration: 0.28,
        ease: "back.out(2.2)",
        onComplete: () => {
          gsap.to(el, {
            opacity: 0,
            y: -18,
            delay: 0.55,
            duration: 0.22,
            onComplete: () => setBanner(null),
          });
        },
      }
    );
    return () => {
      tween.kill();
    };
  }, [banner]);

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;

    const resize = () => {
      const rect = root.getBoundingClientRect();
      setNarrow(rect.width < 560);
      setTouch(window.matchMedia("(pointer: coarse)").matches || rect.width < 480);
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let frame = 0;
    let last = performance.now();

    const loop = (now: number) => {
      const dt = Math.min(48, now - last);
      last = now;
      const engine = engineRef.current;
      const canvas = canvasRef.current;
      const root = rootRef.current;
      const ctx = canvas?.getContext("2d");
      if (engine && canvas && root && ctx) {
        const keys = keysRef.current;
        if (keys.left) {
          keys.dasLeft += dt;
          if (keys.dasLeft > DAS_MS) {
            keys.arrLeft += dt;
            if (keys.arrLeft >= ARR_MS) {
              engine.move(-1);
              keys.arrLeft = 0;
            }
          }
        }
        if (keys.right) {
          keys.dasRight += dt;
          if (keys.dasRight > DAS_MS) {
            keys.arrRight += dt;
            if (keys.arrRight >= ARR_MS) {
              engine.move(1);
              keys.arrRight = 0;
            }
          }
        }

        engine.tick(dt, keys.down);
        effectsRef.current.update(dt);

        const dpr = window.devicePixelRatio || 1;
        const width = canvas.width / dpr;
        const height = canvas.height / dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);

        const isNarrow = width < 560;
        const topHud = isNarrow ? 78 : 16;
        const bottomHud = touch ? 150 : isNarrow ? 18 : 16;
        const sideHud = isNarrow ? 12 : 118;
        const availW = Math.max(80, width - sideHud * 2);
        const availH = Math.max(120, height - topHud - bottomHud);
        const cell = Math.max(
          8,
          Math.floor(Math.min(availW / COLS, availH / VISIBLE_ROWS))
        );
        const boardW = cell * COLS;
        const boardH = cell * VISIBLE_ROWS;
        const originX = (width - boardW) / 2;
        const originY = topHud + (availH - boardH) / 2;
        layoutRef.current = {
          originX,
          originY,
          cell,
          width: boardW,
          height: boardH,
        };

        const dark = document.documentElement.classList.contains("dark");
        drawFrame(
          ctx,
          engine.getSnapshot(),
          effectsRef.current,
          layoutRef.current,
          {
            dark,
            board: dark ? "#0b1220" : "#e8eef5",
            boardEdge: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)",
            grid: dark ? "rgba(148,163,184,0.06)" : "rgba(15,23,42,0.04)",
            ghost: "transparent",
            vignette: dark ? "rgba(2,6,23,0.45)" : "rgba(15,23,42,0.08)",
          },
          now
        );
      }
      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [touch]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isCollapsed) return;
      if (isEditableTarget(event.target)) return;

      const isHelpKey =
        event.key === "?" ||
        event.key === "F1" ||
        (event.key === "/" && event.shiftKey);

      if (event.key === "Escape" && helpOpen) {
        event.preventDefault();
        setHelpOpen(false);
        return;
      }

      if (isHelpKey) {
        event.preventDefault();
        if (event.repeat) return;
        setHelpOpen((open) => {
          const next = !open;
          if (next) {
            engineRef.current?.pause();
            syncHud();
          }
          return next;
        });
        return;
      }

      if (helpOpen) {
        if (CONTROL_KEYS.has(event.key)) event.preventDefault();
        return;
      }

      if (!CONTROL_KEYS.has(event.key)) return;
      event.preventDefault();
      if (event.repeat) return;

      const engine = engineRef.current;
      if (!engine) return;
      const keys = keysRef.current;

      if (event.key === "p" || event.key === "P") {
        engine.togglePause();
        syncHud();
        return;
      }

      if (snapshot.status === "over") return;

      if (event.key === "ArrowLeft" || event.key === "a" || event.key === "A") {
        keys.left = true;
        keys.dasLeft = 0;
        keys.arrLeft = 0;
        engine.move(-1);
      } else if (event.key === "ArrowRight" || event.key === "d" || event.key === "D") {
        keys.right = true;
        keys.dasRight = 0;
        keys.arrRight = 0;
        engine.move(1);
      } else if (event.key === "ArrowDown" || event.key === "s" || event.key === "S") {
        keys.down = true;
      } else if (
        event.key === "ArrowUp" ||
        event.key === "w" ||
        event.key === "W" ||
        event.key === "x" ||
        event.key === "X"
      ) {
        engine.rotate(1);
      } else if (event.key === "z" || event.key === "Z") {
        engine.rotate(-1);
      } else if (event.key === " ") {
        engine.hardDrop();
      } else if (event.key === "c" || event.key === "C" || event.key === "Shift") {
        engine.holdPiece();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const keys = keysRef.current;
      if (event.key === "ArrowLeft" || event.key === "a" || event.key === "A") {
        keys.left = false;
      }
      if (event.key === "ArrowRight" || event.key === "d" || event.key === "D") {
        keys.right = false;
      }
      if (event.key === "ArrowDown" || event.key === "s" || event.key === "S") {
        keys.down = false;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
    };
  }, [helpOpen, isCollapsed, snapshot.status, syncHud]);

  const stats = useMemo(
    () => [
      { label: t("games.tetris.score"), value: snapshot.score },
      { label: t("games.tetris.highScore"), value: snapshot.highScore },
      { label: t("games.tetris.level"), value: snapshot.level },
      { label: t("games.tetris.lines"), value: snapshot.lines },
    ],
    [snapshot.highScore, snapshot.level, snapshot.lines, snapshot.score, t]
  );

  const handleMute = () => {
    const next = tetrisAudio.toggle();
    setMuted(next);
  };

  const handlePause = () => {
    engineRef.current?.togglePause();
    syncHud();
  };

  const toggleHelp = () => {
    setHelpOpen((open) => {
      const next = !open;
      if (next) {
        engineRef.current?.pause();
        syncHud();
      }
      return next;
    });
  };

  const glass =
    "rounded-xl border border-white/40 bg-white/55 backdrop-blur-xl dark:border-white/10 dark:bg-gray-900/55";

  return (
    <div
      ref={rootRef}
      className={cn(
        "relative isolate h-full min-h-0 w-full overflow-hidden touch-none",
        "bg-[radial-gradient(circle_at_top,_rgba(125,211,252,0.18),_transparent_42%)]",
        "dark:bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.12),_transparent_46%)]",
        className
      )}
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full touch-none" />
      {touch && snapshot.status === "playing" && (
        <div
          ref={gestureRef}
          className="absolute inset-x-0 top-0 bottom-[10rem] z-[5] touch-none"
          aria-hidden
        />
      )}

      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-2 z-10 flex gap-2 px-2",
          narrow ? "justify-between" : "justify-between px-3"
        )}
      >
        <div className="pointer-events-auto flex items-start gap-2">
          <PiecePreview
            piece={snapshot.hold}
            label={t("games.tetris.hold")}
            dimmed={!snapshot.canHold}
          />
          {!narrow && (
            <div className={cn(glass, "grid grid-cols-2 gap-x-3 gap-y-1 px-3 py-2")}>
              {stats.map((item) => (
                <div key={item.label} className="min-w-[72px]">
                  <p className="text-[10px] uppercase tracking-wide text-text-tertiary-600 dark:text-gray-400">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold tabular-nums text-text-primary dark:text-white">
                    {item.value}
                  </p>
                </div>
              ))}
              {snapshot.combo > 1 && (
                <p className="col-span-2 text-xs font-medium text-cyan-600 dark:text-cyan-300">
                  {t("games.tetris.comboBanner", { count: snapshot.combo })}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="pointer-events-auto flex items-start gap-2">
          {!narrow &&
            snapshot.queue.map((piece, index) => (
              <PiecePreview
                key={`${piece}-${index}`}
                piece={piece}
                label={index === 0 ? t("games.tetris.next") : `${index + 1}`}
              />
            ))}
          {narrow && (
            <PiecePreview piece={snapshot.queue[0] ?? null} label={t("games.tetris.next")} />
          )}
          <div className="flex flex-col gap-1">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className={cn(glass, "h-8 w-8")}
              onClick={toggleHelp}
              title={t("games.tetris.help")}
            >
              <CircleHelp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className={cn(glass, "h-8 w-8")}
              onClick={handleMute}
              title={muted ? t("games.tetris.unmute") : t("games.tetris.mute")}
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className={cn(glass, "h-8 w-8")}
              onClick={handlePause}
              title={
                snapshot.status === "paused"
                  ? t("games.tetris.resume")
                  : t("games.tetris.pause")
              }
            >
              {snapshot.status === "paused" ? (
                <Play className="h-4 w-4" />
              ) : (
                <Pause className="h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className={cn(glass, "h-8 w-8")}
              onClick={restart}
              title={t("games.tetris.restart")}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {narrow && (
        <div className="pointer-events-none absolute inset-x-0 top-[4.6rem] z-10 flex justify-center px-2">
          <div className={cn(glass, "pointer-events-none flex gap-3 px-3 py-1.5")}>
            {stats.map((item) => (
              <div key={item.label} className="text-center">
                <p className="text-[9px] uppercase tracking-wide text-text-tertiary-600 dark:text-gray-400">
                  {item.label}
                </p>
                <p className="text-xs font-semibold tabular-nums text-text-primary dark:text-white">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {banner && (
        <div
          ref={bannerRef}
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
        >
          <div className="rounded-2xl border border-white/50 bg-white/70 px-6 py-3 text-2xl font-black tracking-[0.2em] text-cyan-700 shadow-2xl backdrop-blur-xl dark:border-cyan-300/30 dark:bg-slate-950/70 dark:text-cyan-200">
            {banner}
          </div>
        </div>
      )}

      {helpOpen && (
        <TetrisHelpOverlay
          showGestures={touch}
          onClose={() => setHelpOpen(false)}
        />
      )}

      {!helpOpen && !isCollapsed && (snapshot.status === "paused" || snapshot.status === "over") && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/25 px-4 backdrop-blur-[2px] dark:bg-black/45">
          <div className={cn(glass, "flex w-full max-w-[260px] flex-col items-center gap-3 p-5 text-center")}>
            <p className="text-lg font-semibold text-text-primary dark:text-white">
              {snapshot.status === "over"
                ? t("games.tetris.gameOver")
                : t("games.tetris.paused")}
            </p>
            <p className="text-sm text-text-tertiary-600 dark:text-gray-400">
              {t("games.tetris.finalScore", { score: snapshot.score })}
            </p>
            <div className="flex gap-2">
              {snapshot.status === "paused" ? (
                <Button size="sm" onClick={handlePause}>
                  {t("games.tetris.resume")}
                </Button>
              ) : (
                <Button size="sm" onClick={restart}>
                  {t("games.tetris.restart")}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {touch && snapshot.status === "playing" && (
        <div className="absolute inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-10 flex items-end justify-between gap-3">
          <div className="grid w-[46%] grid-cols-3 gap-1.5">
            <TouchKey
              label={t("games.tetris.touch.left")}
              onPress={() => engineRef.current?.move(-1)}
              onPressIn={() => {
                keysRef.current.left = true;
                keysRef.current.dasLeft = 0;
                keysRef.current.arrLeft = 0;
              }}
              onPressOut={() => {
                keysRef.current.left = false;
                keysRef.current.dasLeft = 0;
                keysRef.current.arrLeft = 0;
              }}
            >
              <ChevronLeft className="h-6 w-6" />
            </TouchKey>
            <TouchKey
              label={t("games.tetris.touch.down")}
              onPressIn={() => {
                keysRef.current.down = true;
              }}
              onPressOut={() => {
                keysRef.current.down = false;
              }}
            >
              <ChevronDown className="h-6 w-6" />
            </TouchKey>
            <TouchKey
              label={t("games.tetris.touch.right")}
              onPress={() => engineRef.current?.move(1)}
              onPressIn={() => {
                keysRef.current.right = true;
                keysRef.current.dasRight = 0;
                keysRef.current.arrRight = 0;
              }}
              onPressOut={() => {
                keysRef.current.right = false;
                keysRef.current.dasRight = 0;
                keysRef.current.arrRight = 0;
              }}
            >
              <ChevronRight className="h-6 w-6" />
            </TouchKey>
          </div>
          <div className="grid w-[46%] grid-cols-3 gap-1.5">
            <TouchKey
              label={t("games.tetris.touch.rotateLeft")}
              onPress={() => engineRef.current?.rotate(-1)}
            >
              <RotateCcw className="h-5 w-5" />
            </TouchKey>
            <TouchKey
              label={t("games.tetris.touch.rotate")}
              onPress={() => engineRef.current?.rotate(1)}
            >
              <RotateCw className="h-5 w-5" />
            </TouchKey>
            <TouchKey
              label={t("games.tetris.touch.drop")}
              onPress={() => engineRef.current?.hardDrop()}
            >
              <span className="text-[10px] font-bold">{t("games.tetris.touch.drop")}</span>
            </TouchKey>
            <TouchKey
              className="col-span-3"
              label={t("games.tetris.touch.hold")}
              onPress={() => engineRef.current?.holdPiece()}
            >
              <span className="text-[10px] font-bold">{t("games.tetris.touch.hold")}</span>
            </TouchKey>
          </div>
        </div>
      )}
    </div>
  );
}

function TetrisHelpOverlay({
  onClose,
  showGestures,
}: {
  onClose: () => void;
  showGestures: boolean;
}) {
  const { t } = useTranslation();
  const keyboardRows = [
    { keys: "← → / A D", action: t("games.tetris.controls.move") },
    { keys: "↑ / W / X", action: t("games.tetris.controls.rotate") },
    { keys: "Z", action: t("games.tetris.controls.rotateCcw") },
    { keys: "↓ / S", action: t("games.tetris.controls.softDrop") },
    { keys: "Space", action: t("games.tetris.controls.hardDrop") },
    { keys: "C / Shift", action: t("games.tetris.controls.hold") },
    { keys: "P", action: t("games.tetris.controls.pause") },
    { keys: "? / F1", action: t("games.tetris.controls.help") },
  ];
  const gestureRows = [
    { keys: "↔", action: t("games.tetris.gestures.swipeSide") },
    { keys: "•", action: t("games.tetris.gestures.tap") },
    { keys: "↓", action: t("games.tetris.gestures.swipeDown") },
    { keys: "⇣", action: t("games.tetris.gestures.flickDown") },
    { keys: "••", action: t("games.tetris.gestures.doubleTap") },
    { keys: "↑", action: t("games.tetris.gestures.swipeUp") },
  ];
  const rows = showGestures ? gestureRows : keyboardRows;

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/35 px-4 backdrop-blur-[3px] dark:bg-black/55">
      <div className="w-full max-w-[340px] rounded-2xl border border-white/40 bg-white/80 p-5 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-gray-950/80">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-base font-semibold text-text-primary dark:text-white">
              {t("games.tetris.helpTitle")}
            </p>
            <p className="mt-1 text-xs text-text-tertiary-600 dark:text-gray-400">
              {t(showGestures ? "games.tetris.helpHintTouch" : "games.tetris.helpHint")}
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={onClose}>
            {t("games.tetris.resume")}
          </Button>
        </div>
        <ul className="space-y-2">
          {rows.map((row) => (
            <li
              key={row.keys}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span className="rounded-md border border-white/50 bg-white/70 px-2 py-1 font-mono text-[11px] text-text-secondary dark:border-white/10 dark:bg-gray-900/80 dark:text-gray-200">
                {row.keys}
              </span>
              <span className="text-right text-text-primary dark:text-gray-100">
                {row.action}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TouchKey({
  children,
  label,
  className,
  onPress,
  onPressIn,
  onPressOut,
}: {
  children: ReactNode;
  label: string;
  className?: string;
  onPress?: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        "flex h-16 items-center justify-center rounded-xl border border-white/40 bg-white/70 text-text-primary shadow-lg backdrop-blur-xl touch-none select-none",
        "dark:border-white/10 dark:bg-gray-900/70 dark:text-white",
        className
      )}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        onPressIn?.();
        onPress?.();
      }}
      onPointerUp={onPressOut}
      onPointerCancel={onPressOut}
    >
      {children}
    </button>
  );
}
